import React, {
  useEffect,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import SideBar from "../components/sidebar";
import profile from "../img/profile-placeholder.png";
import edit from "../img/edit.png";

const Settings = ({ showSidebar, setPage, isSettings }) => {
  const [nameContainerEdit, setNameConatinerEdit] = useState(false);
  const [
    personalInformationContainerEdit,
    setPersonalInformationConatainerEdit,
  ] = useState(false);
  const [deleteAccount, setDeleteAccount] = useState(false);
  const [profileImage, setProfileImage] = useState(profile);
  const [modalAction, setModalAction] = useState(null);
  const [showConfirmModel, setShowConfirmModel] = useState(false);
  const [textboxValues, setTextboxValues] = useState({});
  const [fetchedUserDetails, setFetchedUserDetails] = useState(null);
  const [isSave, setIsSave] = useState(false);
  const [isCancel, setIsCancel] = useState(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [isPremium, setIsPremium] = useState(null);

  // Storing my refs
  const navigate = useNavigate();
  const nameRef = useRef();
  const firstNameRef = useRef();
  const lastNameRef = useRef();
  const emailRef = useRef();

  useEffect(() => {
    const userID = JSON.parse(localStorage.getItem("sb_id")).id;

    fetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/users/get-details?userID=${userID}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    )
      .then((res) => res.json())
      .then((data) => {
        // keep original fetched structure available and also seed flat fields
        setTextboxValues(data);
        const ud = data?.userDetails?.[0] ?? null;
        if (ud) {
          setTextboxValues((prev) => ({
            ...prev,
            name: ud.name ?? prev.name,
            firstName: ud.firstName ?? prev.firstName,
            lastName: ud.lastName ?? prev.lastName,
            email: ud.email ?? prev.email,
          }));
        }
        setFetchedUserDetails(ud);
      })
      .catch((error) => {
        console.error(error);
      });
  }, [isSettings]);

  useEffect(() => {
    if (!textboxValues.userDetails) return;

    setProfileImage(textboxValues.userDetails[0].profileImage);
    setEmail(textboxValues.userDetails[0].email);
    setFullName(textboxValues.userDetails[0].name);
    setIsPremium(textboxValues.userDetails[0].premium);
    splitFirstAndLastName();
  }, [textboxValues]);

  const splitFirstAndLastName = () => {
    if (!fullName) return;

    if (!fullName || typeof fullName !== "string") return;

    const nameParts = fullName.trim().split(/\s+/); // splits on any whitespace

    const firstName = nameParts[0] || "";
    const lastName = nameParts.slice(1).join(" ") || ""; // supports multiple last names

    setFirstName(firstName);
    setLastName(lastName);
  };

  const handleEditToggle = () => {
    // When entering edit mode, populate inputs from the visible values
    // but don't overwrite existing textboxValues if they already exist.
    if (!nameContainerEdit) {
      setTextboxValues((prev) => ({
        ...prev,
        name: nameRef.current?.textContent ?? prev.name ?? fullName ?? "",
        firstName:
          firstNameRef.current?.textContent ??
          prev.firstName ??
          firstName ??
          "",
        lastName:
          lastNameRef.current?.textContent ?? prev.lastName ?? lastName ?? "",
        email: emailRef.current?.textContent ?? prev.email ?? email ?? "",
      }));
    }

    setNameConatinerEdit((prev) => !prev);
  };

  const handlePersonalInfoToggle = () => {
    // When entering personal-info edit mode, seed inputs from displayed values
    if (!personalInformationContainerEdit) {
      setTextboxValues((prev) => ({
        ...prev,
        firstName:
          firstNameRef.current?.textContent ??
          prev.firstName ??
          firstName ??
          "",
        lastName:
          lastNameRef.current?.textContent ?? prev.lastName ?? lastName ?? "",
        email: emailRef.current?.textContent ?? prev.email ?? email ?? "",
      }));
    }

    setPersonalInformationConatainerEdit((prev) => !prev);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const imageURL = URL.createObjectURL(file);
      setProfileImage(imageURL);
    }
  };

  const handleChange = (e) => {
    const { className, value } = e.target;
    setTextboxValues((prev) => ({
      ...prev,
      [className]: value,
    }));

    if (className === "firstName") {
      setFirstName(value);
    }

    if (className === "lastName") {
      setLastName(value);
    }

    if (className === "name") {
      setFullName(value);
    }
  };

  const renderField = (isEditable, className, value, type = "text", ref) => {
    // Always render email as non-editable text
    if (className === "email") {
      return (
        <div ref={ref} className={className}>
          {textboxValues[className] ?? value ?? ""}
        </div>
      );
    }

    if (isEditable) {
      if (className === "personal-image") {
        return (
          <>
            <img src={profileImage} />
            <input
              className={"personal-image-active"}
              type={type}
              accept="image/*"
              onChange={handleImageChange}
            />
          </>
        );
      }

      return (
        <input
          ref={ref}
          onChange={handleChange}
          className={className}
          value={textboxValues[className] ?? ""}
          type={type}
        />
      );
    }

    if (className === "personal-image") {
      return <img src={profileImage} />;
    }

    return (
      <div ref={ref} className={className}>
        {value}
      </div>
    );
  };

  const displayNameContainer = () => {
    const isEditable = nameContainerEdit;

    return (
      <div className="name-container">
        <div className="personal-image">
          {renderField(isEditable, "personal-image", profile, "file")}
        </div>

        <div className="name-content">
          {renderField(isEditable, "name", fullName || "", "text", nameRef)}
        </div>
      </div>
    );
  };

  const displayPersonalInformationContainer = () => {
    const isEditable = personalInformationContainerEdit;

    return (
      <div className="personal-information-content">
        <ul className="row-header">
          <li>First Name</li>
          <li>Last Name</li>
        </ul>
        <div className="top-row-settings">
          {renderField(
            isEditable,
            "firstName",
            firstName || "",
            "text",
            firstNameRef
          )}
          {renderField(
            isEditable,
            "lastName",
            lastName || "",
            "text",
            lastNameRef
          )}
        </div>
        <ul className="row-header">
          <li>Email Address</li>
          <li>Password</li>
        </ul>
        <div className="bottom-row-settings">
          {renderField(isEditable, "email", email || "", "text", emailRef)}
          <button onClick={handlePasswordChange}>Change Password</button>
        </div>
      </div>
    );
  };

  const displaySaveAlert = () => {
    setModalAction("save");
    setShowConfirmModel(true);
  };

  const displayCancelAlert = () => {
    setModalAction("cancel");
    setShowConfirmModel(true);
  };

  const handlePasswordChange = async () => {
    navigate("/change-password");
    setPage("change-password");
  };

  const handleSave = () => {
    // Check if email has been changed - if so, update in supabase auth
    console.log("Saving changes...");

    // Get textbox values
    const nameValue =
      nameRef.current?.value ??
      nameRef.current?.textContent ??
      textboxValues.name ??
      fullName;
    const firstNameValue =
      firstNameRef.current?.value ??
      firstNameRef.current?.textContent ??
      textboxValues.firstName ??
      firstName;
    const lastNameValue =
      lastNameRef.current?.value ??
      lastNameRef.current?.textContent ??
      textboxValues.lastName ??
      lastName;
    const emailValue =
      emailRef.current?.value ??
      emailRef.current?.textContent ??
      textboxValues.email ??
      email;

    setTextboxValues((prev) => ({
      ...prev,
      profileImage: profileImage,
      name: nameValue,
      firstName: firstNameValue,
      lastName: lastNameValue,
      email: emailValue,
    }));

    // update visible state
    setFullName(nameValue);
    setFirstName(firstNameValue);
    setLastName(lastNameValue);
    setEmail(emailValue);
    updateUserDetailsInDB(); 
    navigate("/dashboard");
    setPage("dashboard");
  };

  const handleCancel = () => {
    // Return textbox values to original fetched state (if available)
    if (fetchedUserDetails) {
      setTextboxValues((prev) => ({
        ...prev,
        profileImage: fetchedUserDetails.profileImage ?? prev.profileImage,
        name: fetchedUserDetails.name ?? prev.name,
        firstName: fetchedUserDetails.firstName ?? prev.firstName,
        lastName: fetchedUserDetails.lastName ?? prev.lastName,
        email: fetchedUserDetails.email ?? prev.email,
      }));

      setFullName(fetchedUserDetails.name ?? fullName);
      setFirstName(fetchedUserDetails.firstName ?? firstName);
      setLastName(fetchedUserDetails.lastName ?? lastName);
      setEmail(fetchedUserDetails.email ?? email);
    }

    setNameConatinerEdit(false);
    setPersonalInformationConatainerEdit(false);
  };

  const handleYes = () => {
    if (isSave === true) {
      handleSave();
      setIsSave(false);
    } else {
      handleCancel();
      setIsCancel(false);
    }
  };

  const handleDeleteButtonPress = () => {
    setDeleteAccount(true);
  };

  // ALSO DELETE FROM STRIPE
  const handleDelete = async (userID) => {
    try {
      const deleteUserCall = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/users/delete-user`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ userID: userID }),
        }
      );

      if (deleteUserCall.ok) {
        if (!isPremium) {
          return "Delete Successful";
        } else {
          // Handle payment removal from stripe + update the user on supabase.
          const removeStripeRequest = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/payment/remove-stripe-customer`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                stripeCustomerId: textboxValues.userDetails[0].stripeCustomerID,
              }),
            }
          );

          if (removeStripeRequest.ok) {
            setIsPremium(false);
            navigate("/dashboard");
            setPage("dashboard");
            return "Delete Successful";
          }
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleSubscriptionChange = async () => {
    if (!isPremium) {
      navigate("/payment");
      setPage("payment");
      setIsPremium(true);
    } else {
      // Handle payment removal from stripe + update the user on supabase.
      const removeStripeRequest = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/payment/remove-stripe-customer`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            stripeCustomerId: textboxValues.userDetails[0].stripeCustomerID,
          }),
        }
      );

      if (removeStripeRequest.ok) {
        alert("Subscription cancelled successfully.");
        setIsPremium(false);
        navigate("/dashboard");
        setPage("dashboard");
      }
    }
  };

  const updateUserDetailsInDB = async () => {
    const userID = JSON.parse(localStorage.getItem("sb_id")).id;

    try {
      const updateRequest = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/users/update-user`,
        {
          method: "PUT",  
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            userID: userID,
            name: textboxValues.firstName + " " + textboxValues.lastName,
            profileImage: profileImage,
          }),
        }
      );

      if (updateRequest.message.status === "User updated successfully") {
        console.log("User details updated successfully.");
        console.log(updateRequest.message.user)
      } else {
        console.error("Failed to update user details.");
      }
    } catch (error) {
      console.error("Error updating user details:", error);
    }
  };

  return (
    <div className="settings-container">
      {showSidebar || <SideBar setPage={setPage} />}

      <div className="settings-content">
        <div className="settings-header">
          My Profile
          <button
            className="edit-button"
            onClick={() => {
              handleEditToggle();
            }}
          >
            Edit <img src={edit} />
          </button>
        </div>

        {displayNameContainer()}

        <div className="personal-information">
          <div className="personal-information-header">
            Personal Information
            <button className="edit-button" onClick={handlePersonalInfoToggle}>
              Edit <img src={edit} />
            </button>
          </div>

          {displayPersonalInformationContainer()}
        </div>

        <div className="account-preferences">
          <div className="change-subscription">
            <div className="change-subscription-header">
              Change Subscription Plan
            </div>

            <button
              className="change-subscription-button"
              onClick={handleSubscriptionChange}
            >
              Change Subscription Plan
            </button>
          </div>

          <div className="delete-account">
            <div className="delete-account-header">Delete Account</div>

            <button
              className="delete-account-button"
              onClick={handleDeleteButtonPress}
            >
              Delete Account
            </button>
          </div>
        </div>

        {showConfirmModel && (
          <div className="modal-container">
            <div className="modal">
              <p>Are you sure you want to {modalAction} your changes?</p>
              <div className="modal-buttons">
                <button
                  className="yes-button"
                  onClick={() => {
                    setShowConfirmModel(false);
                    handleYes();
                  }}
                >
                  Yes
                </button>
                <button
                  className="no-button"
                  onClick={() => {
                    setShowConfirmModel(false);
                    isCancel === true ? handleYes() : handleCancel();
                  }}
                >
                  No
                </button>
              </div>
            </div>
          </div>
        )}

        {deleteAccount && (
          <div className="modal-container">
            <div className="modal">
              <p>Are you sure you want to delete your TelemetryLab account?</p>
              <div className="modal-buttons">
                <button
                  className="yes-button"
                  onClick={async () => {
                    const deleteRequest = await handleDelete(
                      JSON.parse(
                        localStorage.getItem(
                          "sb_id"
                        )
                      ).id
                    );

                    if (deleteRequest === "Delete Successful") {
                      navigate("/");
                      setPage("landing");
                      setDeleteAccount(false);
                      return;
                    } else {
                      alert("Internal Server Error");
                    }
                  }}
                >
                  Yes
                </button>
                <button
                  className="no-button"
                  onClick={() => {
                    setDeleteAccount(false);
                  }}
                >
                  No
                </button>
              </div>
            </div>
          </div>
        )}

        <ul className="settings-buttons">
          <li
            className="save-button"
            onClick={() => {
              setIsSave(true);
              displaySaveAlert();
            }}
          >
            Save
          </li>
          <li
            className="cancel-button"
            onClick={() => {
              setIsCancel(true);
              displayCancelAlert();
            }}
          >
            Cancel
          </li>
        </ul>
      </div>
    </div>
  );
};

export default Settings;
